import type { AudioTrackSummary } from './types';

const AUDIO_TRACK_TYPE = 'AUDIO';

export function normalizeLanguage(raw: string | undefined): string {
  if (!raw) return '';
  return raw.trim().replace(/_/g, '-').replace(/;.*$/, '').toLowerCase();
}

export function summarizeAudioTracks(
  tracks: ReadonlyArray<{
    trackId: number;
    type: string;
    language?: string;
    name?: string;
  }>,
): AudioTrackSummary[] {
  return tracks
    .filter((t) => t.type === AUDIO_TRACK_TYPE)
    .map((t) => {
      const language = t.language ?? '';
      const normalizedLang = normalizeLanguage(language);
      return {
        id: t.trackId,
        language,
        normalizedLang,
        name: t.name && t.name.length > 0 ? t.name : language,
        isDefault: false,
      };
    });
}

function primarySubtag(lang: string): string {
  const idx = lang.indexOf('-');
  return idx === -1 ? lang : lang.slice(0, idx);
}

export function findBestAudioTrackForLanguage(
  tracks: ReadonlyArray<AudioTrackSummary>,
  requestedLanguage: string,
): AudioTrackSummary | null {
  if (tracks.length === 0) return null;

  const normalized = normalizeLanguage(requestedLanguage);

  if (normalized.length > 0) {
    const exact = tracks.find((t) => t.normalizedLang === normalized);
    if (exact) return exact;

    const requestedPrimary = primarySubtag(normalized);
    if (requestedPrimary.length > 0) {
      const subtagMatch = tracks.find(
        (t) =>
          primarySubtag(t.normalizedLang) === requestedPrimary && t.normalizedLang.length > 0,
      );
      if (subtagMatch) return subtagMatch;
    }
  }

  const defaultTrack = tracks.find((t) => t.isDefault);
  if (defaultTrack) return defaultTrack;

  return tracks[0] ?? null;
}
